/*
 * UploadContext.jsx — globalna kolejka uploadów plików działająca W TLE.
 *
 * Cel: użytkownik zaczyna wgrywać duże pliki w module Pliki, przełącza się
 * na inną zakładkę, wraca — pliki wciąż się wgrywają, floating indicator
 * pokazuje postęp na każdej stronie.
 *
 * Architektura:
 *   - Provider trzyma kolejkę jako useState + useRef (nie odmontowuje się przy zmianie tab)
 *   - `enqueue(files, meta)` — dodaje pliki do kolejki; uruchamia worker jeśli nie działa
 *   - Worker robi upload PO KOLEI (nie parallel, żeby nie zabić serwera)
 *   - Emituje `window` event 'drive-upload-done' po zakończeniu każdego pliku
 *     (FilesTab słucha i odświeża listę)
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const UploadContext = createContext(null);

export const useUpload = () => {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUpload must be inside <UploadProvider>');
  return ctx;
};

export const UploadProvider = ({ children }) => {
  // Kolejka: [{ id, file, folderId, location, uploadedBy, folderName, status, progress, error }]
  const [queue, setQueue] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const workerRunning = useRef(false);
  const cancelRefs = useRef({}); // id -> AbortController

  const updateItem = useCallback((id, patch) => {
    setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const processNext = useCallback(async () => {
    // Chunked upload dla plików >80 MB — omija limit proxy k8s ~100 MB
    const uploadChunked = async (item, controller) => {
      // 1) init
      const initRes = await axios.post(`${API}/api/drive/upload/init`, {
        location: item.location,
        folderId: item.folderId,
        filename: item.file.name,
        size: item.file.size,
        mime: item.file.type || 'application/octet-stream',
        uploadedBy: item.uploadedBy || '',
      }, { signal: controller.signal });
      const { uploadId, chunkSize } = initRes.data;

      // 2) chunks — sekwencyjnie z progresem
      const totalChunks = Math.ceil(item.file.size / chunkSize);
      let uploaded = 0;
      for (let i = 0; i < totalChunks; i++) {
        if (controller.signal.aborted) throw new Error('cancelled');
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, item.file.size);
        const blob = item.file.slice(start, end);
        const fd = new FormData();
        fd.append('uploadId', uploadId);
        fd.append('chunkIndex', String(i));
        fd.append('chunk', blob, `${i}.part`);
        await axios.post(`${API}/api/drive/upload/chunk`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: controller.signal,
          timeout: 10 * 60 * 1000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (evt) => {
            const chunkProgress = evt.total ? evt.loaded : blob.size;
            const totalProgress = uploaded + chunkProgress;
            updateItem(item.id, {
              progress: Math.min(99, Math.round((totalProgress / item.file.size) * 100)),
            });
          },
        });
        uploaded += blob.size;
      }

      // 3) complete
      const completeFd = new FormData();
      completeFd.append('uploadId', uploadId);
      await axios.post(`${API}/api/drive/upload/complete`, completeFd, {
        signal: controller.signal,
        timeout: 15 * 60 * 1000, // scalanie + upload do storage dla 5GB pliku
      });
    };

    // znajdź kolejny pending
    let current;
    setQueue((q) => {
      current = q.find((it) => it.status === 'pending');
      return q;
    });
    // Racing hack — pobierz z aktualnego stanu przez useState updater
    const item = await new Promise((resolve) => {
      setQueue((q) => {
        resolve(q.find((it) => it.status === 'pending'));
        return q;
      });
    });
    if (!item) {
      workerRunning.current = false;
      return;
    }
    // Ustaw uploading
    updateItem(item.id, { status: 'uploading', progress: 0 });

    const controller = new AbortController();
    cancelRefs.current[item.id] = controller;

    try {
      const CHUNK_THRESHOLD = 80 * 1024 * 1024; // 80 MB — powyżej używamy chunked upload (proxy k8s ma limit ~100 MB)
      if (item.file.size > CHUNK_THRESHOLD) {
        await uploadChunked(item, controller);
      } else {
        const fd = new FormData();
        fd.append('file', item.file);
        fd.append('location', item.location);
        fd.append('folderId', item.folderId);
        fd.append('uploadedBy', item.uploadedBy || '');
        await axios.post(`${API}/api/drive/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: controller.signal,
          timeout: 30 * 60 * 1000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (evt) => {
            if (evt.total) {
              updateItem(item.id, { progress: Math.round((evt.loaded / evt.total) * 100) });
            }
          },
        });
      }
      updateItem(item.id, { status: 'done', progress: 100 });
      // Powiadom FilesTab żeby odświeżył listę
      window.dispatchEvent(new CustomEvent('drive-upload-done', {
        detail: { fileName: item.file.name, folderId: item.folderId, location: item.location },
      }));
    } catch (e) {
      if (controller.signal.aborted) {
        updateItem(item.id, { status: 'cancelled', error: 'Anulowano' });
      } else {
        const detail = e.response?.data?.detail || e.message || 'Błąd wgrywania';
        updateItem(item.id, { status: 'error', error: String(detail) });
        toast.error(`❌ ${item.file.name}: ${detail}`);
      }
    } finally {
      delete cancelRefs.current[item.id];
    }
    // Rekurencyjnie następny
    processNext();
  }, [updateItem]);

  const startWorker = useCallback(() => {
    if (workerRunning.current) return;
    workerRunning.current = true;
    processNext();
  }, [processNext]);

  const enqueue = useCallback((files, meta) => {
    if (!files || files.length === 0) return;
    const newItems = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      folderId: meta.folderId,
      folderName: meta.folderName || '',
      location: meta.location,
      uploadedBy: meta.uploadedBy || '',
      status: 'pending',
      progress: 0,
      error: null,
    }));
    setQueue((q) => [...q, ...newItems]);
    setMinimized(false);
    // startWorker po mikrotasku, żeby state zdążyło się zaktualizować
    setTimeout(startWorker, 0);
  }, [startWorker]);

  const cancel = useCallback((id) => {
    const c = cancelRefs.current[id];
    if (c) c.abort();
    setQueue((q) => q.map((it) => (it.id === id && it.status === 'pending' ? { ...it, status: 'cancelled', error: 'Anulowano' } : it)));
  }, []);

  const clearFinished = useCallback(() => {
    setQueue((q) => q.filter((it) => it.status === 'pending' || it.status === 'uploading'));
  }, []);

  // ostrzeżenie przy zamknięciu karty jeśli są aktywne uploady
  useEffect(() => {
    const beforeUnload = (e) => {
      const active = queue.some((it) => it.status === 'pending' || it.status === 'uploading');
      if (active) {
        e.preventDefault();
        e.returnValue = 'Trwa wgrywanie plików. Jeśli zamkniesz kartę, upload zostanie przerwany.';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [queue]);

  const stats = {
    total: queue.length,
    done: queue.filter((it) => it.status === 'done').length,
    active: queue.filter((it) => it.status === 'pending' || it.status === 'uploading').length,
    error: queue.filter((it) => it.status === 'error').length,
  };

  return (
    <UploadContext.Provider value={{ queue, enqueue, cancel, clearFinished, stats, minimized, setMinimized }}>
      {children}
    </UploadContext.Provider>
  );
};

export default UploadProvider;
